import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type {
  UserProfile,
  UserProfilePatch,
} from "@resume-cloudflare/shared";
import { MapPin, Save } from "lucide-react";
import { useProfile, usePatchProfile } from "../lib/profile";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Skeleton } from "../components/ui/Skeleton";

// Personal/demographic/links/auth fields. Demographic answers are free-text
// because each ATS phrases the EEOC question slightly differently — the PR2
// classifier maps these onto the form's actual options at fill time.

interface FormState {
  full_name: string;
  phone: string;
  city_state: string;
  salary_expectation: string;
  gender: string;
  race_ethnicity: string;
  veteran_status: string;
  disability_status: string;
  lgbtq_status: string;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  website_url: string;
  work_authorization: string;
  // Tri-state radio. "" = Decline / unspecified.
  requires_sponsorship: "" | "yes" | "no";
}

const EMPTY: FormState = {
  full_name: "",
  phone: "",
  city_state: "",
  salary_expectation: "",
  gender: "",
  race_ethnicity: "",
  veteran_status: "",
  disability_status: "",
  lgbtq_status: "",
  linkedin_url: "",
  github_url: "",
  portfolio_url: "",
  website_url: "",
  work_authorization: "",
  requires_sponsorship: "",
};

function fromServer(p: UserProfile | null): FormState {
  if (!p) return { ...EMPTY };
  return {
    full_name: p.full_name ?? "",
    phone: p.phone ?? "",
    city_state: p.city_state ?? "",
    salary_expectation: p.salary_expectation ?? "",
    gender: p.gender ?? "",
    race_ethnicity: p.race_ethnicity ?? "",
    veteran_status: p.veteran_status ?? "",
    disability_status: p.disability_status ?? "",
    lgbtq_status: p.lgbtq_status ?? "",
    linkedin_url: p.linkedin_url ?? "",
    github_url: p.github_url ?? "",
    portfolio_url: p.portfolio_url ?? "",
    website_url: p.website_url ?? "",
    work_authorization: p.work_authorization ?? "",
    requires_sponsorship:
      p.requires_sponsorship === null
        ? ""
        : p.requires_sponsorship
          ? "yes"
          : "no",
  };
}

function toPatch(s: FormState): UserProfilePatch {
  return {
    full_name: s.full_name,
    phone: s.phone,
    city_state: s.city_state,
    salary_expectation: s.salary_expectation,
    gender: s.gender,
    race_ethnicity: s.race_ethnicity,
    veteran_status: s.veteran_status,
    disability_status: s.disability_status,
    lgbtq_status: s.lgbtq_status,
    linkedin_url: s.linkedin_url,
    github_url: s.github_url,
    portfolio_url: s.portfolio_url,
    website_url: s.website_url,
    work_authorization: s.work_authorization,
    requires_sponsorship:
      s.requires_sponsorship === ""
        ? null
        : s.requires_sponsorship === "yes",
  };
}

export default function Profile() {
  const profile = useProfile();
  const patch = usePatchProfile("Profile saved.");

  const serverState = useMemo(
    () => fromServer(profile.data?.profile ?? null),
    [profile.data?.profile],
  );
  const [local, setLocal] = useState<FormState>(serverState);

  useEffect(() => setLocal(serverState), [serverState]);

  const dirty = JSON.stringify(local) !== JSON.stringify(serverState);
  const canSave = dirty && !patch.isPending;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setLocal((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Drives application form auto-fill (Session 4). One-time setup;
          fields that don't apply can be left blank.
        </p>
      </header>

      <Card>
        <CardHeader
          title="Personal"
          description="Full name, phone, location header line, and salary expectations."
        />
        <CardBody>
          {profile.isLoading ? (
            <FieldSkeletons rows={4} />
          ) : profile.error ? (
            <EmptyState
              tone="danger"
              title="Couldn't load profile"
              description="Try refreshing in a moment."
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Full name"
                value={local.full_name}
                onChange={(v) => set("full_name", v)}
                placeholder="Jane Doe"
              />
              <Field
                label="Phone"
                value={local.phone}
                onChange={(v) => set("phone", v)}
                placeholder="+1 555 555 5555"
              />
              <Field
                label="City, region"
                value={local.city_state}
                onChange={(v) => set("city_state", v)}
                placeholder="Austin, TX"
              />
              <Field
                label="Salary expectation"
                value={local.salary_expectation}
                onChange={(v) => set("salary_expectation", v)}
                placeholder="$220k base, negotiable"
              />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Links"
          description="Surfaced as URL fields on most application forms."
        />
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="LinkedIn URL"
              value={local.linkedin_url}
              onChange={(v) => set("linkedin_url", v)}
              placeholder="https://linkedin.com/in/…"
            />
            <Field
              label="GitHub URL"
              value={local.github_url}
              onChange={(v) => set("github_url", v)}
              placeholder="https://github.com/…"
            />
            <Field
              label="Portfolio URL"
              value={local.portfolio_url}
              onChange={(v) => set("portfolio_url", v)}
              placeholder="https://…"
            />
            <Field
              label="Personal website"
              value={local.website_url}
              onChange={(v) => set("website_url", v)}
              placeholder="https://…"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Work authorization"
          description="Free text — describe your situation in plain language."
        />
        <CardBody className="space-y-3">
          <Field
            label="Work authorization"
            value={local.work_authorization}
            onChange={(v) => set("work_authorization", v)}
            placeholder="US citizen / Canadian PR + TN-eligible / EU national"
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">
              Requires visa sponsorship?
            </label>
            <div className="flex flex-wrap gap-3 text-sm">
              {[
                ["", "Decline to state"],
                ["no", "No"],
                ["yes", "Yes"],
              ].map(([value, label]) => (
                <label
                  key={value}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-neutral-800 bg-neutral-900/40 px-2.5 py-1 hover:border-neutral-700"
                >
                  <input
                    type="radio"
                    name="requires_sponsorship"
                    checked={local.requires_sponsorship === value}
                    onChange={() =>
                      set("requires_sponsorship", value as FormState["requires_sponsorship"])
                    }
                    className="h-3 w-3 accent-accent-500"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Demographics"
          description="EEOC-shaped questions. Each is optional; leave blank to decline. Mapped onto each ATS's actual options at fill time."
        />
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Gender"
              value={local.gender}
              onChange={(v) => set("gender", v)}
              options={[
                "",
                "Woman",
                "Man",
                "Non-binary",
                "Prefer to self-describe",
                "Decline to state",
              ]}
            />
            <SelectField
              label="Race / ethnicity"
              value={local.race_ethnicity}
              onChange={(v) => set("race_ethnicity", v)}
              options={[
                "",
                "American Indian or Alaska Native",
                "Asian",
                "Black or African American",
                "Hispanic or Latino",
                "Native Hawaiian or Other Pacific Islander",
                "White",
                "Two or more races",
                "Decline to state",
              ]}
            />
            <SelectField
              label="Veteran status"
              value={local.veteran_status}
              onChange={(v) => set("veteran_status", v)}
              options={[
                "",
                "I am a protected veteran",
                "I am not a protected veteran",
                "Decline to state",
              ]}
            />
            <SelectField
              label="Disability status"
              value={local.disability_status}
              onChange={(v) => set("disability_status", v)}
              options={[
                "",
                "Yes, I have a disability",
                "No, I do not have a disability",
                "Decline to state",
              ]}
            />
            <SelectField
              label="LGBTQ+ status"
              value={local.lgbtq_status}
              onChange={(v) => set("lgbtq_status", v)}
              options={["", "Yes", "No", "Prefer not to say"]}
            />
          </div>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
        <div className="text-xs text-neutral-500">
          Location preferences live on{" "}
          <Link
            to="/profile/location"
            className="inline-flex items-center gap-1 text-neutral-300 underline-offset-4 hover:underline"
          >
            <MapPin size={12} aria-hidden /> their own page
          </Link>
          .
        </div>
        <Button
          variant="primary"
          size="sm"
          loading={patch.isPending}
          disabled={!canSave}
          leadingIcon={<Save size={12} aria-hidden />}
          onClick={() => patch.mutate(toPatch(local))}
        >
          Save profile
        </Button>
      </div>
    </section>
  );
}

function FieldSkeletons({ rows }: { rows: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-400">
        {label}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-400">
        {label}
      </label>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt === "" ? "Decline / not yet specified" : opt}
          </option>
        ))}
      </Select>
    </div>
  );
}
